import React, { useEffect, useState } from 'react';
import { Modal } from 'reactstrap';
import { Badge, Button, DescriptionList, Disclosure, EmptyState, Loader } from '../../../../ui';
import { StatTile, TeacherStatusBadge } from './ui-pending';
import './AiClassQA.scss';
import { getQaEntries, classRecording } from '../../../../Api/apiUtils';
import { toast } from 'react-toastify';

interface AiClassQAProps {
  teacherId: string;
}

interface AssessmentRow {
  assessment_max_score: number;
  assessment_score: number;
  quiz_max_score: number;
  quiz_score: number;
  weighted_score: number;
}

interface QaItem {
  qa_parameter: string;
  status: string;
  evidence: string;
}

interface QaEntry {
  class_id: number;
  class_date: string;
  class_duration_minutes?: number;
  class_transcript?: string;
  is_red_flag?: boolean;
  red_flag_reason?: string;
  red_flag_type?: string;
  student_name?: string;
  recording_count?: number;
  qa_summary?: string;
  qa_list?: QaItem[];
  assessment_rows?: AssessmentRow[];
  teacher_cred_id?: number;
}

interface QaPagination {
  has_next: boolean;
  has_previous: boolean;
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

const PAGE_SIZE = 10;

const AiClassQA: React.FC<AiClassQAProps> = ({ teacherId }) => {
  const [entries, setEntries] = useState<QaEntry[]>([]);
  const [pagination, setPagination] = useState<QaPagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [recordingLoadingId, setRecordingLoadingId] = useState<number | null>(null);
  const [recordingsModalEntry, setRecordingsModalEntry] = useState<QaEntry | null>(null);
  const [recordingLinks, setRecordingLinks] = useState<string[]>([]);

  useEffect(() => {
    setPage(1);
  }, [teacherId]);

  useEffect(() => {
    const fetchData = async () => {
      if (!teacherId) return;
      setLoading(true);
      try {
        const data = await getQaEntries(teacherId, page, PAGE_SIZE);
        const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
        const filtered = list.filter(
          (item: QaEntry) =>
            typeof item.is_red_flag !== 'undefined' &&
            item.class_date,
        );
        setEntries(filtered);
        setPagination(data?.pagination ?? null);
        setExpandedId(null);
      } catch {
        toast.error('Failed to load AI Class QA entries');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [teacherId, page]);

  const toggleExpand = (classId: number) => {
    setExpandedId(prev => (prev === classId ? null : classId));
  };

  const handleGetRecording = async (entry: QaEntry) => {
    if (!entry.teacher_cred_id || !entry.class_id) {
      toast.error('Recording details not available');
      return;
    }
    setRecordingLoadingId(entry.class_id);
    try {
      const res = await classRecording(entry.teacher_cred_id, entry.class_id);
      let links: string[] = [];
      if (Array.isArray(res)) {
        // API can return array of plain URLs or objects
        links = res
          .map((item: any) =>
            typeof item === 'string' ? item : item?.recordingUrl || item?.url || '',
          )
          .filter((u: string) => !!u);
      } else if (typeof res === 'string') {
        links = [res];
      }

      if (!links.length) {
        toast.error('No recording found');
      } else {
        // Always show modal with list, even for a single recording
        setRecordingLinks(links);
        setRecordingsModalEntry(entry);
      }
    } catch {
      toast.error('Failed to fetch recording link');
    } finally {
      setRecordingLoadingId(null);
    }
  };

  const computeScores = (assessment_rows?: AssessmentRow[]) => {
    if (!assessment_rows || assessment_rows.length === 0) return null;
    const row = assessment_rows[0];
    const theory =
      row.quiz_max_score > 0 ? Math.round((row.quiz_score / row.quiz_max_score) * 100) : null;
    const practical =
      row.assessment_max_score > 0
        ? Math.round((row.assessment_score / row.assessment_max_score) * 100)
        : null;
    const total = row.weighted_score ? Math.round(row.weighted_score * 100) : null;
    return { theory, practical, total };
  };

  if (loading) {
    return <Loader label="Loading AI QA entries…" />;
  }

  if (!entries.length) {
    return <EmptyState title="No AI QA entries found for this teacher." />;
  }

  const closeRecordings = () => {
    setRecordingsModalEntry(null);
    setRecordingLinks([]);
  };

  return (
    <div className="tp-aiqa">
      {entries.map(entry => {
        const isRed = !!entry.is_red_flag;
        const scores = computeScores(entry.assessment_rows);
        const isOpen = expandedId === entry.class_id;
        return (
          <div key={entry.class_id} className={`tp-aiqa__entry ${isRed ? 'tp-aiqa__entry--red' : 'tp-aiqa__entry--ok'}`}>
            <Disclosure
              open={isOpen}
              onToggle={() => toggleExpand(entry.class_id)}
              summary={
                <div className="tp-aiqa__summary">
                  <div className="tp-aiqa__summary-main">
                    <div className="tp-aiqa__title">
                      <span className="ds-strong">{entry.class_date}</span>
                      <span className="ds-text-muted">Red Flag:</span>
                      <Badge tone={isRed ? 'danger' : 'success'}>{isRed ? 'Yes' : 'No'}</Badge>
                    </div>
                    <div className="tp-aiqa__meta ds-text-subtle">
                      Student: {entry.student_name || '—'} | Duration:{' '}
                      {entry.class_duration_minutes ?? '—'} min | Recordings:{' '}
                      {entry.recording_count ?? 0}
                    </div>
                  </div>
                  <div className="tp-aiqa__summary-side">
                    {isRed && entry.red_flag_type && <Badge tone="danger">{entry.red_flag_type}</Badge>}
                    <span className="tp-aiqa__toggle">{isOpen ? 'Hide' : 'View'} Details</span>
                  </div>
                </div>
              }
            >
              {isRed && entry.red_flag_reason && (
                <section className="tp-aiqa__section tp-aiqa__section--danger">
                  <h3 className="tp-aiqa__heading">Red Flag Details</h3>
                  <p className="tp-aiqa__text">{entry.red_flag_reason}</p>
                </section>
              )}

              <section className="tp-aiqa__section">
                <h3 className="tp-aiqa__heading">QA Summary</h3>
                <div className="tp-aiqa__text tp-aiqa__text--pre-line">{entry.qa_summary || '—'}</div>
                <div className="tp-aiqa__recording">
                  <DescriptionList
                    items={[
                      { label: 'Duration', value: `${entry.class_duration_minutes ?? '—'} min` },
                      { label: 'Recordings', value: entry.recording_count ?? 0 },
                    ]}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleGetRecording(entry)}
                    disabled={recordingLoadingId === entry.class_id}
                  >
                    {recordingLoadingId === entry.class_id ? 'Loading...' : 'Get Recording'}
                  </Button>
                </div>
                {scores && (
                  <div className="tp-aiqa__assessment" role="group" aria-label="Assessment">
                    <StatTile label="Theory" value={scores.theory != null ? `${scores.theory}%` : '—'} />
                    <StatTile label="Practical" value={scores.practical != null ? `${scores.practical}%` : '—'} />
                    <StatTile label="Total" tone="primary" value={scores.total != null ? `${scores.total}%` : '—'} />
                  </div>
                )}
              </section>

              {entry.qa_list && entry.qa_list.length > 0 && (
                <section className="tp-aiqa__section">
                  <h3 className="tp-aiqa__heading">QA Details</h3>
                  <ul className="tp-aiqa__qa-list">
                    {entry.qa_list.map((q, idx) => (
                      <li key={idx} className="tp-aiqa__qa-item">
                        <div className="tp-aiqa__qa-head">
                          <span className="ds-strong">{q.qa_parameter}</span>
                          <TeacherStatusBadge status={q.status} label={q.status} />
                        </div>
                        <div className="tp-aiqa__qa-evidence ds-text-muted">{q.evidence}</div>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {entry.class_transcript && (
                <section className="tp-aiqa__section">
                  <h3 className="tp-aiqa__heading">Class transcript</h3>
                  <div className="tp-aiqa__transcript">{entry.class_transcript}</div>
                </section>
              )}
            </Disclosure>
          </div>
        );
      })}
      {pagination && pagination.total_pages > 1 && (
        <div className="tp-aiqa__pager">
          <span className="ds-text-subtle">
            Page {pagination.page} of {pagination.total_pages} · {pagination.total} classes
          </span>
          <div className="tp-aiqa__pager-actions">
            <Button
              size="sm"
              variant="secondary"
              disabled={loading || !pagination.has_previous}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={loading || !pagination.has_next}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      {/* Recordings dialog */}
      <Modal
        isOpen={!!recordingsModalEntry && recordingLinks.length > 0}
        toggle={closeRecordings}
        centered
        className="ds-dialog"
      >
        {recordingsModalEntry && (
          <>
            <h2 className="ds-dialog__title">Recordings for class {recordingsModalEntry.class_id}</h2>
            <ul className="tp-aiqa__recordings">
              {recordingLinks.map((url, idx) => (
                <li key={idx} className="tp-aiqa__recordings-item">
                  <span>Recording {idx + 1}</span>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(url);
                        toast.success(`Recording ${idx + 1} link copied`);
                      } catch {
                        toast.error('Failed to copy link');
                      }
                    }}
                  >
                    Copy Link
                  </Button>
                </li>
              ))}
            </ul>
            <div className="ds-dialog__actions">
              <Button variant="secondary" onClick={closeRecordings}>Close</Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default AiClassQA;
